import React, { useState, useEffect } from 'react';
import { FiSliders, FiUsers, FiUser, FiCalendar, FiX } from 'react-icons/fi';
import { BsBuilding } from 'react-icons/bs';
import api from '../../../../api';
import { getRoundRobinBaseUrl } from '../../../../loadBalancer';
import { useAuth } from '../../../Layouts/context/Authcontext';
import Flatpickr from 'react-flatpickr';
import 'flatpickr/dist/flatpickr.css';

const QAPerformanceReportsFilter = ({ isChecked, onFilterChange }) => {
  const defaultFilters = {
    client: '',
    scope: '',
    employeeId: '',
    startDate: '',
    endDate: '',
    status: '',
  };

  const [filters, setFilters] = useState(defaultFilters);
  const [showFilters, setShowFilters] = useState(false);
  const [clientList, setClientList] = useState([]);
  const [scopeList, setScopeList] = useState([]);
  const [employeeList, setEmployeeList] = useState([]);
  const { accessToken } = useAuth();
  const [dateRange, setDateRange] = useState([null, null]);

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const baseUrl = getRoundRobinBaseUrl();
        const res = await api.get(`${baseUrl}/uploadedFile/getClient`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        const formatted = res.data.data.map((c) => ({ label: c.client, value: c._id }));
        setClientList(formatted);
      } catch (err) {
        console.error('Error fetching clients:', err);
      }
    };
    if (!isChecked) fetchClients();
  }, [isChecked, accessToken]);

  useEffect(() => {
    const fetchScopes = async () => {
      try {
        const baseUrl = getRoundRobinBaseUrl();
        const res = await api.get(`${baseUrl}/uploadedFile/getScopebasedOnClient?clientId=${filters.client}`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        const formatted = res.data.data.map((s) => ({ label: s.displayName, value: s._id }));
        setScopeList(formatted);
      } catch (err) {
        console.error('Error fetching scopes:', err);
      }
    };
    if (filters.client) fetchScopes();
  }, [filters.client, accessToken]);

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const baseUrl = getRoundRobinBaseUrl();
        const res = await api.get(`${baseUrl}/uploadedFile/getAuditorBasedOnScope?scopeId=${filters.scope}`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        const formatted = res.data.data.map((emp) => ({ label: emp.FirstName, value: emp.employeeID }));
        setEmployeeList(formatted);
      } catch (err) {
        console.error('Error fetching employees:', err);
      }
    };
    if (filters.scope) fetchEmployees();
  }, [filters.scope, accessToken]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const updated = { ...filters, [name]: value };
    setFilters(updated);
    const formattedFilters = {
      ...updated,
      startDate: dateRange[0] ? formatDateToMMDDYYYY(dateRange[0]) : '',
      endDate: dateRange[1] ? formatDateToMMDDYYYY(dateRange[1]) : '',
    };
    onFilterChange && onFilterChange(formattedFilters);
  };

  const handleDateChange = (dates) => {
    const [start, end] = dates;
    setDateRange(dates);
    const updatedFilters = {
      ...filters,
      startDate: start ? start.toISOString().split('T')[0] : '',
      endDate: end ? end.toISOString().split('T')[0] : '',
    };
    setFilters(updatedFilters);
    const formattedFilters = {
      ...updatedFilters,
      startDate: start ? formatDateToMMDDYYYY(start) : '',
      endDate: end ? formatDateToMMDDYYYY(end) : '',
    };
    onFilterChange && onFilterChange(formattedFilters);
  };

  const formatDateToMMDDYYYY = (dateStr) => {
    const date = new Date(dateStr);
    if (isNaN(date)) return '';
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    const year = date.getFullYear();
    return `${month}/${day}/${year}`;
  };

  const handleClear = () => {
    setFilters(defaultFilters);
    onFilterChange && onFilterChange(defaultFilters);
    setScopeList([]);
    setEmployeeList([]);
    setDateRange([null, null]);
  };

  const Field = ({ label, name, icon, children }) => (
    <div className="flex flex-col space-y-1">
      <label className="text-sm font-semibold text-[#1A2B5C]">{label}</label>
      <div className="relative group">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#1A2B5C]">{icon}</span>
        <div className="pl-10 pr-3 py-1.5 w-full border border-[#1A2B5C]/40 bg-white rounded-md focus-within:ring-2 focus-within:ring-[#EE1D23] transition-all shadow-sm hover:border-[#1A2B5C]">
          {children}
        </div>
      </div>
    </div>
  );

  return (
    <div className="relative">
      <div className="relative flex justify-end mb-2">
        <button
          onClick={() => setShowFilters(!showFilters)}
          className="text-white bg-gradient-to-r from-[#1A2B5C] to-[#EE1D23] hover:from-[#1A2B5C]/90 hover:to-[#EE1D23]/90 transition px-4 py-2 rounded-full shadow-lg mt-4 mr-4"
          title="Toggle Filters"
        >
          <FiSliders className={`text-lg transition-transform duration-300 ${showFilters ? 'rotate-90 scale-110' : ''}`} />
        </button>
      </div>

      <div
        className={`transition-all duration-500 ease-in-out transform ${
          showFilters ? 'translate-x-0 opacity-100' : 'translate-x-full opacity-0'
        } fixed right-4 top-24 w-full max-w-[550px] max-h-[80vh] overflow-y-auto bg-white/60 backdrop-blur-lg border border-white/30 shadow-xl rounded-2xl px-6 py-6 z-50`}
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-[#1A2B5C]">Filter Reports</h3>
          <button
            onClick={() => setShowFilters(false)}
            className="text-[#EE1D23] hover:text-red-700 text-xl transition"
            title="Close"
          >
            <FiX />
          </button>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <Field label="Client" name="client" icon={<BsBuilding />}>
            <select
              name="client"
              value={filters.client}
              onChange={handleChange}
              className="w-full appearance-none bg-white text-[#1A2B5C] text-sm font-medium px-3 py-2 rounded-md border border-[#1A2B5C]/40 focus:outline-none focus:ring-2 focus:ring-[#1A2B5C] transition-all shadow-sm hover:border-[#1A2B5C]"
            >
              <option value="" className="text-gray-400 font-normal">
                Select Client
              </option>
              {clientList.map((c) => (
                <option key={c.value} value={c.value}>
                  {c.label}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Scope" name="scope" icon={<FiUsers />}>
            <select
              name="scope"
              value={filters.scope}
              onChange={handleChange}
              className="w-full appearance-none bg-white text-[#1A2B5C] text-sm font-medium px-3 py-2 rounded-md border border-[#1A2B5C]/40 focus:outline-none focus:ring-2 focus:ring-[#1A2B5C] transition-all shadow-sm hover:border-[#1A2B5C]"
            >
              <option value="" className="text-gray-400 font-normal">
                Select Scope
              </option>
              {scopeList.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </Field>

          <Field label="User" name="user" icon={<FiUser />}>
            <select
              name="user"
              value={filters.user}
              onChange={handleChange}
              className="w-full appearance-none bg-white text-[#1A2B5C] text-sm font-medium px-3 py-2 rounded-md border border-[#1A2B5C]/40 focus:outline-none focus:ring-2 focus:ring-[#1A2B5C] transition-all shadow-sm hover:border-[#1A2B5C]"
            >
              <option value="" className="text-gray-400 font-normal">
                Select User
              </option>
              {employeeList.map((u) => (
                <option key={u.value} value={u.value}>
                  {u.label}
                </option>
              ))}
            </select>
          </Field>

          <Field label="Select Date Range" name="dateRange" icon={<FiCalendar />}>
            <Flatpickr
              value={dateRange}
              onChange={handleDateChange}
              options={{
                mode: 'range',
                dateFormat: 'm/d/Y',
                allowInput: true,
                closeOnSelect: false,
              }}
              className="w-full px-3 py-2 border border-[#1A2B5C]/40 rounded-md text-sm text-[#1A2B5C] placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#EE1D23] shadow-sm"
            />
          </Field>
        </div>

        <div className="flex justify-end mt-6">
          <button
            onClick={handleClear}
            className="text-sm px-4 py-2 bg-white text-[#EE1D23] border border-[#EE1D23] hover:bg-[#EE1D23] hover:text-white transition rounded-full shadow-sm"
          >
            Clear Filters
          </button>
        </div>
      </div>
    </div>
  );
};

export default QAPerformanceReportsFilter;
